import type { BlockRegistry, BlockSpec } from '@lp/shared';
import { layoutPrimitives, Section, Stack, Grid } from './primitives/layout.js';
import { Columns } from './primitives/columns.js';
import {
  contentPrimitives,
  ButtonBlock,
  DividerBlock,
  EmbedBlock,
  Heading,
  IconBlock,
  ImageBlock,
  LinkBlock,
  ListBlock,
  TextBlock,
} from './primitives/content.js';
import { HeroSplit } from './landing/hero.js';
import { CtaBand } from './landing/ctaBand.js';
import { FeatureGrid } from './landing/featureGrid.js';
import { FeatureCard } from './landing/featureCard.js';

/**
 * Every block the builder offers, in two tiers.
 *
 * **Primitives** are the composable elements — containers and content — that let someone
 * build any section from scratch. **Presets** are pre-arranged starting points. Both emit the
 * same `lp-box` + utility classes, so a preset is a head start rather than a black box: what
 * it produces is exactly what you could have assembled yourself.
 *
 * Block ids are persisted inside documents, so renaming one is a migration, not a rename.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- specs are heterogeneous by design
const PRESETS: BlockSpec<any>[] = [HeroSplit, FeatureGrid, FeatureCard, CtaBand];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SPECS: BlockSpec<any>[] = [...layoutPrimitives, ...contentPrimitives, ...PRESETS];

const BY_ID = new Map<string, BlockSpec<never>>();
for (const spec of SPECS) {
  if (BY_ID.has(spec.id)) throw new Error(`Duplicate block id "${spec.id}".`);
  BY_ID.set(spec.id, spec as BlockSpec<never>);
}

/** Which panel group a block appears under. */
export type BlockGroup = 'layout' | 'content' | 'presets';

const LAYOUT_IDS = new Set(layoutPrimitives.map((s) => s.id));
const PRESET_IDS = new Set(PRESETS.map((s) => s.id));

export function groupOf(id: string): BlockGroup {
  if (LAYOUT_IDS.has(id)) return 'layout';
  if (PRESET_IDS.has(id)) return 'presets';
  return 'content';
}

/** Blocks that only make sense inside another block's slot. */
const NESTED_ONLY = new Set(['FeatureCard']);

export const blockRegistry: BlockRegistry = {
  get(id) {
    const spec = BY_ID.get(id);
    if (!spec) {
      throw new Error(
        `Unknown block "${id}". A document referencing it was probably saved by a newer ` +
          `version of the builder, or the block was renamed without a migration.`,
      );
    }
    return spec;
  },
  has: (id) => BY_ID.has(id),
  all: () => [...BY_ID.values()],
  byCategory: (cat) =>
    [...BY_ID.values()].filter(
      (s) => (s.category === cat || s.category === 'both') && !NESTED_ONLY.has(s.id),
    ),
};

/** Every spec, including the nested-only ones the top-level picker hides. */
export const allBlockSpecs = (): BlockSpec<never>[] => [...BY_ID.values()];

export { Section, Columns, Stack, Grid };
export {
  Heading,
  TextBlock,
  ButtonBlock,
  ImageBlock,
  IconBlock,
  ListBlock,
  LinkBlock,
  DividerBlock,
  EmbedBlock,
};
export { HeroSplit, FeatureGrid, FeatureCard, CtaBand };
export { layoutPrimitives, contentPrimitives };
